import classNames from 'classnames';
import get from 'lodash.get';
import pick from 'lodash.pick';
import {isValidPhoneNumber} from 'libphonenumber-js';
import {AUTH_TYPES, RECIPIENT_TYPES, TFA_TYPES} from '../../../../constants';
import {RECIPIENT_PLACEHOLDERS} from '@api/constants';
import {getIsCurrentUserEmail, getNormalizedEmail} from '@utils/emailMatching';

function getIsSenderRecipient(recipient, ownerId) {
    return (ownerId && recipient.user_id === ownerId)
        || recipient.placeholder?.toLowerCase() === RECIPIENT_PLACEHOLDERS.OWNER;
}

/**
 * @param {string[]} [ownerAliasEmails] normalized alias emails of the owner; pass only when the owner is the current user
 */
function getIsOwnerEmail(email, ownerEmail, ownerAliasEmails = []) {
    const ownerEmails = [getNormalizedEmail(ownerEmail), ...ownerAliasEmails].filter(Boolean);
    return getIsCurrentUserEmail(email, ownerEmails);
}

export function getCleanedRecipient(role) {
    if (!role) {
        return undefined;
    }

    return pick(role, [
        'id',
        'first_name',
        'last_name',
        'email',
        'organisation',
        'title',
        'phone',
        'language',
        'placeholder',
        'order',
        'change_recipient',
        'signed_documents_in_mail',
        'personal_message',
        'two_factor_authentication',
        'group_id',
        'is_locked',
        'attributes'
    ]);
}

export const MENU_PLACEMENT = {
    left: 'left',
    right: 'right',
    topLeft: 'top-left',
    topRight: 'top-right',
    topCenter: 'top-center',
    bottomLeft: 'bottom-left',
    bottomRight: 'bottom-right',
    bottomCenter: 'bottom-center'

};

const TFA_TO_AUTH_TYPE = {
    [TFA_TYPES.SMS]: AUTH_TYPES.SMS,
    [TFA_TYPES.QA]: AUTH_TYPES.CHALLENGE,
    [TFA_TYPES.SSO]: AUTH_TYPES.SSO
};

function getAuthTypeFromTfa(tfa) {
    if (!tfa?.type) return AUTH_TYPES.NONE;
    return TFA_TO_AUTH_TYPE[tfa.type] || AUTH_TYPES.NONE;
}

function getSsoAlias(tfa) {
    return tfa?.alias || get(tfa, 'challenges[0].alias', '') || '';
}

function isRecipientValid(role, type) {
    if (type === RECIPIENT_TYPES.PLACEHOLDER && !role.placeholder) {
        return false;
    }
    if (type === RECIPIENT_TYPES.SIGNER) {
        const authScheme = getAuthTypeFromTfa(role.two_factor_authentication);
        const signerAuthPhone = authScheme === AUTH_TYPES.SMS
            ? get(role, 'two_factor_authentication.challenges[0].phone', '') : null;

        return !!role.first_name && !!role.last_name && !!role.email
            && (authScheme === AUTH_TYPES.SMS ? isValidPhoneNumber(signerAuthPhone) : true);
    }
    if (type === RECIPIENT_TYPES.GROUP) {
        return !!role.group_id;
    }
    return true;
}

function getInternationalNumber(rawNumber = '') {
    const number = rawNumber || '';
    const digitNumber = number.replace(/\D/g, '');

    return digitNumber ? `+${digitNumber}` : '';
}

/**
 * @param {Record<string, string[]>} [currentUserGroupIdsByAccountId] resolved group ids per account, account key absent while unresolved
 * @param {string[]} [currentUserEmails] normalized primary, JWT and confirmed alias emails; when omitted only user.email is compared
 */
function checkSignerIsCurrentUser(signer, user, groupMembersByGroupId = {}, currentUserGroupIdsByAccountId = {}, currentUserEmails) {
    if (signer?.email?.toLowerCase() === user?.email?.toLowerCase() || signer?.id === user?.id) return true;
    if (currentUserEmails && getIsCurrentUserEmail(signer?.email, currentUserEmails)) return true;

    const groupId = signer?.group?.id ?? signer?.group_id ?? signer?.groupId;
    if (!groupId) return false;

    const isGroupMemberInAnyAccount = Object.values(currentUserGroupIdsByAccountId || {})
        .some((groupIds) => Array.isArray(groupIds) && groupIds.includes(groupId));
    if (isGroupMemberInAnyAccount) return true;

    const members = groupMembersByGroupId?.[groupId];
    if (!Array.isArray(members) || members.length === 0) return false;

    return members.some((member) => (currentUserEmails
        ? getIsCurrentUserEmail(member?.email, currentUserEmails)
        : member?.email && member.email.toLowerCase() === user?.email?.toLowerCase()));
}

function checkIsDefaultError(error) {
    switch (error) {
        case 'esl.error_500.oops_broken':
            return true;
        case 'esl.error.internal.default':
            return true;
        default:
            return false;
    }
}

function getSortedRecipients(roles = []) {
    return [...roles].sort((a, b) => a.order - b.order);
}

function getDefaultRecipient(additionalData) {
    return {
        first_name: null,
        last_name: null,
        email: null,
        organisation: null,
        title: null,
        phone: null,
        language: '',
        placeholder: '',
        order: 0,
        change_recipient: false,
        signed_documents_in_mail: false,
        personal_message: null,
        two_factor_authentication: null,
        group_id: null,
        is_locked: false,
        attributes: [],
        ...additionalData
    };
}

function getDefaultSignerRecipient(additionalData) {
    return getDefaultRecipient({
        first_name: '',
        last_name: '',
        email: '',
        organisation: '',
        title: '',
        phone: '',
        ...additionalData
    });
}

function getDefaultPlaceholderRecipient(additionalData) {
    return getDefaultRecipient(additionalData);
}

function getDefaultGroupRecipient(additionalData) {
    return getDefaultRecipient({group_id: '', ...additionalData});
}

function getSignerRecipientName(namedRoles, ownerId) {
    const signers = namedRoles
        .filter((role) => !getIsSenderRecipient(role, ownerId));
    const maxIndex = signers.length + 1 || 1;

    for (let i = 1; i <= maxIndex; i += 1) {
        if (!signers.some(({placeholder}) => placeholder === `Signer${i}`)) {
            return `Signer${i}`;
        }
    }

    return `Signer${maxIndex}`;
}

function getDefaultRecipients(roles, isSenderVisible, userAttributes, ownerId) {
    if (roles.length <= 1 && !isSenderVisible) {
        return [...roles, getDefaultSignerRecipient({
            placeholder: getSignerRecipientName(roles, ownerId),
            change_recipient: Boolean(userAttributes?.allow_change_signer)
        })];
    }

    return roles;
}

function getOrderedRecipients(rolesToOrder, shouldSetOrder, isSenderVisible, ownerId) {
    const sender = rolesToOrder
        .filter((role) => getIsSenderRecipient(role, ownerId));
    const filteredSigners = rolesToOrder
        .filter((role) => !getIsSenderRecipient(role, ownerId));

    const reorderedRoles = isSenderVisible ? rolesToOrder : [...sender, ...filteredSigners];

    return reorderedRoles.map((role, idx) => ({
        ...role,
        order: shouldSetOrder ? idx : 0
    }));
}

function getResortedRecipientsIndexes(rolesToResort = [], isSenderVisible, ownerId) {
    const sender = rolesToResort
        .filter((role) => getIsSenderRecipient(role, ownerId));
    const signers = rolesToResort
        .filter((role) => !getIsSenderRecipient(role, ownerId));
    const roles = isSenderVisible ? [...rolesToResort] : signers;
    const orders = roles.map(({order}) => order)
        .sort((a, b) => a - b)
        .filter((e, idx, array) => e !== array[idx - 1]);
    const resortedRoles = roles.map((role) => ({
        ...role,
        order: orders.indexOf(role.order)
    }));

    return getSortedRecipients(isSenderVisible ? resortedRoles : [...sender, ...resortedRoles]);
}

function getIndex({
    startListIndex,
    endListIndex,
    listIndex,
    currentRoleOrder,
    dragEndRoleOrder
}) {
    if (listIndex === startListIndex) {
        return dragEndRoleOrder;
    }
    if (startListIndex > endListIndex) {
        if (listIndex >= endListIndex && listIndex <= startListIndex) {
            return currentRoleOrder + 1;
        }
    }
    if (startListIndex < endListIndex) {
        if (listIndex >= startListIndex && listIndex <= endListIndex) {
            return currentRoleOrder - 1;
        }
    }

    return currentRoleOrder;
}

function getReorderedRecipients(rolesToReorder, startIndex, endIndex, isSenderVisible, isFlowSet, ownerId) {
    const resetedOrderRoles = isFlowSet ? getOrderedRecipients(rolesToReorder, true, isSenderVisible, ownerId) : rolesToReorder;
    const dragEndRoleOrder = resetedOrderRoles[endIndex]?.order;
    const shiftedRoles = resetedOrderRoles.map((role, listIndex) => ({
        ...role,
        order: getIndex({
            startListIndex: startIndex,
            endListIndex: endIndex,
            listIndex,
            currentRoleOrder: role.order,
            dragEndRoleOrder
        })
    }));

    return getResortedRecipientsIndexes(shiftedRoles, isSenderVisible, ownerId);
}

function replaceSignerWithSender(rolesToReplace, senderIndex, idToReplace, shouldSetOrder, ownerId) {
    const replacedRoles = [...rolesToReplace];
    const [sender] = replacedRoles.splice(senderIndex, 1);
    const replacedRole = replacedRoles.find(({id}) => {
        if (idToReplace) {
            return id === idToReplace;
        }
        return !id;
    }) || {};
    const replacedIdx = replacedRoles.indexOf(replacedRole);

    replacedRoles.splice(replacedIdx, 1, {
        ...sender,
        order: replacedRole.order || 0
    });

    return !shouldSetOrder ? replacedRoles : getResortedRecipientsIndexes(replacedRoles, true, ownerId);
}

function replaceSenderWithSigner(rolesToReplace, updatedRole, senderIndex, shouldSetOrder, ownerId) {
    const replacedRoles = [...rolesToReplace];

    replacedRoles.splice(senderIndex, 0, updatedRole);

    const replacedSenderIndex = replacedRoles
        .findIndex((role) => getIsSenderRecipient(role, ownerId));
    const [sender] = replacedRoles.splice(replacedSenderIndex, 1);

    replacedRoles.splice(0, 0, {...sender, order: 0});

    return !shouldSetOrder ? replacedRoles : getResortedRecipientsIndexes(replacedRoles, false, ownerId);
}

function getUnsavedRecipientError(updateRoleError, deleteRoleError) {
    if (checkIsDefaultError(updateRoleError)) {
        return 'esl.partials.dialogs.designer_unsaved_navigate_away.title';
    }
    return updateRoleError || deleteRoleError;
}

function getRecipientWithoutSmsAuthentication(recipient) {
    if (recipient?.two_factor_authentication?.type !== TFA_TYPES.SMS) {
        return recipient;
    }

    return {...recipient, two_factor_authentication: null};
}

function getRecipientByType(type, role, filteredRoles) {
    const commonFields = {
        id: role.id,
        order: role.order,
        change_recipient: role.change_recipient,
        placeholder: role.placeholder || getSignerRecipientName(filteredRoles),
        personal_message: role.personal_message,
        signed_documents_in_mail: role.signed_documents_in_mail,
        two_factor_authentication: role.two_factor_authentication ?? null
    };

    switch (type) {
        case RECIPIENT_TYPES.PLACEHOLDER:
            return getDefaultPlaceholderRecipient(getRecipientWithoutSmsAuthentication(commonFields));
        case RECIPIENT_TYPES.GROUP:
            return getDefaultGroupRecipient(commonFields);
        default:
            return getDefaultSignerRecipient(commonFields);
    }
}
function clearPhoneFromRecipient(role) {
    return {
        ...role,
        phone: '',
        two_factor_authentication: role.two_factor_authentication
            ? {...role.two_factor_authentication, challenges: []}
            : null
    };
}

/**
 * @param {string[]} [currentUserEmails] normalized primary, JWT and confirmed alias emails; when omitted only user.email is compared
 */
function configureRecipientFromSuggestion(recipient, suggestion, user = {}, currentUserEmails) {
    const updatedRecipient = {
        ...recipient,
        first_name: suggestion.firstName || recipient.first_name,
        last_name: suggestion.lastName || recipient.last_name,
        email: suggestion.email || recipient.email,
        organisation: suggestion.company || '',
        title: suggestion.title || '',
        phone: suggestion.phone || '',
        two_factor_authentication: null
    };

    const isOwnerAsSender = getIsCurrentUserEmail(updatedRecipient.email, currentUserEmails ?? [getNormalizedEmail(user?.email)]);
    if (isOwnerAsSender) {
        return clearPhoneFromRecipient(updatedRecipient);
    }

    return updatedRecipient;
}

function getRecipientGroupName(recipient, groups = []) {
    const groupId = recipient?.group_id ?? recipient?.groupId;
    if (!groupId || !groups?.length) return null;
    return groups.find(({id}) => id === groupId)?.name || null;
}

function getSignerDisplayName(signer, groups = []) {
    const groupName = getRecipientGroupName(signer, groups);
    const fullName = signer.firstName && signer.lastName ? `${signer.firstName} ${signer.lastName}` : '';
    return groupName || fullName || signer.placeholder || '';
}

function getRecipientType(recipient, isSender) {
    const groupId = recipient.group_id ?? recipient.groupId;
    const hasGroupId = groupId !== null && groupId !== undefined;
    if (hasGroupId) {
        return RECIPIENT_TYPES.GROUP;
    }

    if (recipient.isPlaceholder === true) {
        return RECIPIENT_TYPES.PLACEHOLDER;
    }

    if (recipient.email === null) {
        return RECIPIENT_TYPES.PLACEHOLDER;
    }

    return isSender ? RECIPIENT_TYPES.SENDER : RECIPIENT_TYPES.SIGNER;
}

function getRecipientStatusClassName(recipient, className) {
    const statusClasses = {
        'is-ordered': recipient.isOrdered,
        'is-completed': recipient.isCompleted,
        'is-pending': recipient.isPending,
        'is-declined': recipient.isDeclined,
        'is-expires-soon': recipient.isExpiresSoon,
        'is-on-top': recipient.isOnTop,
        'is-last-on-top': recipient.isLastOnTop,
        'is-expired': recipient.isExpired,
        'is-transaction-declined': recipient.isTransactionDeclined,
        'is-transaction-expired': recipient.isTransactionExpired,
        'is-transaction-completed': recipient.isTransactionCompleted
    };
    return classNames(className, statusClasses);
}

function getTypeOptions({
    type,
    isSenderVisible,
    shouldHideGroupOption,
    shouldShowIntro
}) {
    return Object.keys(RECIPIENT_TYPES)
        .filter((recipientType) => {
            const isSameType = RECIPIENT_TYPES[recipientType] === type;
            if (RECIPIENT_TYPES[recipientType] === RECIPIENT_TYPES.SENDER) {
                return !isSenderVisible && !isSameType;
            }
            if (RECIPIENT_TYPES[recipientType] === RECIPIENT_TYPES.GROUP) {
                return !shouldHideGroupOption && !isSameType;
            }
            if (RECIPIENT_TYPES[recipientType] === RECIPIENT_TYPES.PLACEHOLDER) {
                return !shouldShowIntro && !isSameType;
            }
            return !isSameType;
        });
}

function changeSenderType({
    newType, roles, isOrdered, initialRecipient, ownerId
}) {
    const filteredRoles = roles.filter(({id}) => id);
    let updatedRole;
    const sender = roles.find((recipient) => getIsSenderRecipient(recipient, ownerId));
    const updatedOrder = isOrdered ? sender.order : 0;

    const initialRecipientData = initialRecipient || {};
    const initialRecipientId = initialRecipientData.id;
    const initialRecipientOrder = initialRecipientData.order;
    const wasNotSenderInitially = !getIsSenderRecipient(initialRecipientData, ownerId) && initialRecipientId;
    const initialRecipientPlaceholder = initialRecipientData.placeholder;
    const recipientPlaceholder = wasNotSenderInitially ? initialRecipientPlaceholder : getSignerRecipientName(filteredRoles, ownerId);
    const recipientOrder = wasNotSenderInitially ? initialRecipientOrder : updatedOrder;
    const defaultRecipient = {
        order: recipientOrder,
        placeholder: recipientPlaceholder
    };

    if (wasNotSenderInitially) {
        defaultRecipient.id = initialRecipientId;
    }

    switch (newType) {
        case RECIPIENT_TYPES.PLACEHOLDER:
            updatedRole = getDefaultPlaceholderRecipient(defaultRecipient);
            break;
        case RECIPIENT_TYPES.GROUP:
            updatedRole = getDefaultGroupRecipient(defaultRecipient);
            break;
        default:
            updatedRole = getDefaultSignerRecipient(defaultRecipient);
            break;
    }

    return updatedRole;
}

function changeSignerToSender({recipient, recipients, isOrdered, ownerId}) {
    const senderIndex = recipients.findIndex((r) => getIsSenderRecipient(r, ownerId));
    const replacedRecipients = replaceSignerWithSender(
        recipients,
        senderIndex,
        recipient.id,
        isOrdered,
        ownerId
    );
    const sender = replacedRecipients.find((r) => getIsSenderRecipient(r, ownerId));

    return {sender, replacedRecipients, recipient};
}

function changeRecipientType({
    newType, recipient, recipients
}) {
    const filteredRecipients = recipient.id ? recipients.filter(({id}) => id) : recipients;
    const updatedRecipient = getRecipientByType(newType, recipient, filteredRecipients);

    return updatedRecipient;
}

export function getSenderFilteredRecipients(roles, isSenderVisible, ownerId) {
    return roles.filter((role) => {
        if (!isSenderVisible && getIsSenderRecipient(role, ownerId)) {
            return false;
        }
        return true;
    });
}

function getAuthButtonTestId(key) {
    if (!key) return '';
    if (key === AUTH_TYPES.NONE) {
        return 'test-email-authentication';
    }
    return `test-${key.toLowerCase()}-authentication`;
}

export {
    getIsSenderRecipient,
    getIsOwnerEmail,
    isRecipientValid,
    getInternationalNumber,
    checkSignerIsCurrentUser,
    checkIsDefaultError,
    getAuthTypeFromTfa,
    getSsoAlias,
    getSortedRecipients,
    getDefaultRecipient,
    getDefaultSignerRecipient,
    getDefaultPlaceholderRecipient,
    getDefaultGroupRecipient,
    getSignerRecipientName,
    getDefaultRecipients,
    getOrderedRecipients,
    getReorderedRecipients,
    replaceSignerWithSender,
    replaceSenderWithSigner,
    getUnsavedRecipientError,
    getResortedRecipientsIndexes,
    getRecipientByType,
    getRecipientWithoutSmsAuthentication,
    configureRecipientFromSuggestion,
    getRecipientType,
    getRecipientGroupName,
    getSignerDisplayName,
    getRecipientStatusClassName,
    getTypeOptions,
    changeSenderType,
    changeSignerToSender,
    changeRecipientType,
    getAuthButtonTestId
};
